import React, { useEffect, useState, useContext, useCallback } from "react";
import { Tabs, LegacyCard as Card, Layout } from "@shopify/polaris";
import { useQuery, useMutation } from "@apollo/client";
import { useSearchParams } from "react-router-dom";

import { withFeature, withErrorBoundary } from "lib/hoc";
import { ResourceList, Banner, PermissionBanner } from "lib/components";
import { baseHelper, errorHelper } from "lib/helpers";
import { QuickAction, QuickReport } from "app/reports";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";

import BULK_INVITE_VIEW from "app/invitations/apollo/subscription";
import { GET_BULK_INVITE_LIST, GET_BULK_EMAIL_TEMPLATE } from "app/invitations/apollo/query";
import { UPDATE_INVITATION_EMAIL_STATUS, BULK_INVITATION_BY_DATA } from "app/invitations/apollo/mutation";
import InvitationListView from "app/invitations/generic/subFeature/invitationListView";

const OperatorInvitation = () => {
  const { currentUser, cms, isShopifyCustomOperator } = useContext(PrivateContext);
  const { isReadOnly } = currentUser || {};

  const [searchParams, setSearchParams] = useSearchParams();
  const prevFilter = {
    search: searchParams.get("search") || null,
    filter: searchParams.get("filter") || null,
    page: parseInt(searchParams.get("page"), 10) || 1,
    perPage: parseInt(searchParams.get("perPage"), 10) || 10,
    tab: parseInt(searchParams.get("tab"), 10) || 0,
    sort_name: searchParams.get("sort_name"),
    sort_order: searchParams.get("sort_order"),
  };

  const [selectedTab, setSelectedTab] = useState(prevFilter.tab);
  const [queryValue, setQueryValue] = useState(prevFilter.search);
  const [currentPage, setCurrentPage] = useState(prevFilter.page);
  const [selectedLimit, setSelectedLimit] = useState(prevFilter.perPage);
  const selectedSort = prevFilter.sort_name
    ? `${prevFilter.sort_name}_${prevFilter.sort_order}`
    : constant.CREATED_DESC;
  const [sortValue, setSortValue] = useState(selectedSort);
  const selectedButtonIndex = null;
  const [bulkInvitation, setBulkInvitation] = useState([]);
  const [activePopover, setActivePopover] = useState({});

  const [totalVendor, setTotalVendor] = useState(0);
  const [banner, setBanner] = useState({ isOpen: false, title: "", status: "" });

  const tabs = [
    {
      id: "all-vendors",
      content: cms("tabs.all"),
      accessibilityLabel: "All vendors",
      panelID: "all-vendors-content",
      value: "all",
    },
    {
      id: "invited-vendors",
      content: cms("tabs.invited"),
      accessibilityLabel: "invited vendors",
      panelID: "invited-vendors-content",
      value: "invited",
    },
    {
      id: "joined-vendors",
      content: cms("tabs.joined"),
      accessibilityLabel: "joined vendors",
      panelID: "joined-vendors-content",
      value: "joined",
    },
    {
      id: "revoked-vendors",
      content: cms("tabs.revoked"),
      accessibilityLabel: "revoked vendors",
      panelID: "revoked-vendors-content",
      value: "revoked",
    },
    {
      id: "resent-vendors",
      content: cms("tabs.resent"),
      accessibilityLabel: "resent vendors",
      panelID: "resent-vendors-content",
      value: "resent",
    },
    {
      id: "pending-vendors",
      content: cms("tabs.pending"),
      accessibilityLabel: "pending vendors",
      panelID: "pending-vendors-content",
      value: "pending",
    },
  ];
  const sortOptions = [
    { label: cms("resourceList.sortOption.sortByDateAsc"), value: constant.CREATED_ASC },
    { label: cms("resourceList.sortOption.sortByDateDesc"), value: constant.CREATED_DESC },
  ];

  const dataToFetch = {
    search: prevFilter.search,
    filter: prevFilter.filter,
    page: prevFilter.page,
    perPage: prevFilter.perPage,
    sort_name: prevFilter.sort_name || constant.CREATED_AT,
    sort_order: prevFilter.sort_order || constant.DESC,
  };

  const { data, loading, error, subscribeToMore, refetch } = useQuery(GET_BULK_INVITE_LIST, {
    variables: { input: dataToFetch },
  });

  const [revokeInvitation, { loading: loadingRevoke }] = useMutation(UPDATE_INVITATION_EMAIL_STATUS, {
    refetchQueries: [{ query: GET_BULK_INVITE_LIST, variables: { input: dataToFetch } }],
  });
  const [resendInvitation, { loading: loadingResend }] = useMutation(BULK_INVITATION_BY_DATA, {
    // refetchQueries: [{ query: GET_BULK_INVITE_LIST, variables: { input: dataToFetch } }],
    onCompleted: () => {
      refetch();
      setBanner({ title: cms("message.requestSuccess"), status: constant.SUCCESS, isOpen: true });
    },
  });
  const { data: emailTplData, error: emailTplError, loading: emailTplLoading } = useQuery(GET_BULK_EMAIL_TEMPLATE);

  useEffect(() => {
    subscribeToMore({
      document: BULK_INVITE_VIEW,
      updateQuery: () => refetch(),
    });
  }, [subscribeToMore, refetch]);

  useEffect(() => {
    if (data) {
      const listData = baseHelper.getResponseData(data, "getBulkInviteList") || {};
      setBulkInvitation(listData.bulkInviteList || []);
      setTotalVendor(listData.count || 0);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(error) });
    }
  }, [error]);

  useEffect(() => {
    if (emailTplError) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(emailTplError) });
    }
  }, [emailTplError]);

  const handleTabChange = useCallback(
    (newIndex) => {
      setSelectedTab(newIndex);
      const tabValue = tabs[newIndex].value;
      setSearchParams((prev) => {
        prev.set("tab", String(newIndex));
        prev.set("filter", tabValue);
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams, tabs]
  );

  const handleQueryChange = useCallback(
    (value) => {
      setQueryValue(value);
      setSearchParams((prev) => {
        if (value) prev.set("search", value);
        else prev.delete("search");
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const handlePerPage = useCallback(
    (newPage) => {
      setCurrentPage(newPage);
      setSearchParams((prev) => {
        prev.set("page", String(newPage));
        return prev;
      });
    },
    [setSearchParams]
  );

  const handleSortChange = useCallback(
    (selected) => {
      setSortValue(selected);
      const [name, order] = selected.split("_");
      setSearchParams((prev) => {
        prev.set("sort_name", name);
        prev.set("sort_order", order);
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const renderItem = (item) => (
    <InvitationListView
      key={item.id}
      item={item}
      cancelButtonLoading={loadingRevoke}
      resendButtonLoading={loadingResend}
      onCancel={(email) => revokeInvitation({ variables: { input: { email } } })}
      onResend={(email) => {
        const templateData = baseHelper.getResponseData(emailTplData, "getBulkEmailTemplate") || {};
        resendInvitation({
          variables: {
            input: {
              emails: [{ email }],
              isResent: true,
              message: {
                message: templateData.message,
                subject: templateData.subject,
              },
              isSingleInvite: true,
              isReadOnly,
            },
          },
        });
      }}
      selectedButtonIndex={selectedButtonIndex}
      activePopover={activePopover}
      setActivePopover={setActivePopover}
    />
  );

  return (
    <>
      {!isShopifyCustomOperator && <PermissionBanner />}

      {banner.isOpen && (
        <>
          <Banner
            status={banner.status}
            title={banner.title}
            isOpen={banner.isOpen}
            onDismiss={() => setBanner((b) => ({ ...b, isOpen: false }))}
          />
          <br />
        </>
      )}
      <QuickReport />
      <QuickAction />

      <Layout>
        <Layout.Section>
          <Card>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
            <ResourceList
              resourceName={{ singular: cms("resourceList.singular"), plural: cms("resourceList.plural") }}
              items={bulkInvitation}
              renderItem={renderItem}
              loading={loading || emailTplLoading}
              queryValue={queryValue}
              onQueryChange={handleQueryChange}
              onQueryClear={() => handleQueryChange("")}
              sortOptions={sortOptions}
              sortValue={sortValue}
              onSortChange={handleSortChange}
              page={currentPage}
              perPage={selectedLimit}
              setPage={handlePerPage}
              setPerPage={(val) => {
                setSelectedLimit(val);
                handlePerPage(1);
              }}
              count={totalVendor}
            />
          </Card>
        </Layout.Section>
      </Layout>
    </>
  );
};

export default withFeature(withErrorBoundary(OperatorInvitation), { feature: constant.INVITATION });
